import {
  CssColor,
  CssFont,
  StyleType,
  wixColor,
  wixFont,
} from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParams,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';
import type { CustomCssVarsFn } from '@wix/yoshi-flow-editor';
import { TextFieldTheme } from 'wix-ui-tpa/cssVars';

import { PageAlignment } from '../../types';

type TPAParams = Parameters<typeof customCssVars>[0]['styleParams'];

export type IStylesParams = {
  pageAlignment: StyleParamType.String;
  pageBgColor: StyleParamType.Color;
  borderColor: StyleParamType.Color;
  borderWidth: StyleParamType.Number;
  settingsBgColor: StyleParamType.Color;
  fieldTheme: StyleParamType.String;
  fieldLabelColor: StyleParamType.Color;
  inputFieldsColor: StyleParamType.Color;
  fieldBorderColor: StyleParamType.Color;
  inputFieldsFont: StyleParamType.Font;
  fieldBorderWidth: StyleParamType.Number;
  fieldLabelFont: StyleParamType.Font;
  descriptionFont: StyleParamType.Font;
  descriptionColor: StyleParamType.Color;
  buttonTextFont: StyleParamType.Font;
  primaryButtonTextColor: StyleParamType.Color;
  buttonBgColor: StyleParamType.Color;
  buttonBorderColor: StyleParamType.Color;
  buttonBorderWidth: StyleParamType.Number;
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  sectionTitleColor: StyleParamType.Color;
  fieldRowGap: StyleParamType.Number;
  fieldColGap: StyleParamType.Number;
  fieldsBgColor: StyleParamType.Color;
  dividerColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  settingsFont: StyleParamType.Font;
  settingsColor: StyleParamType.Color;
  buttonCornerRadius: StyleParamType.Number;
  inputCornerRadius: StyleParamType.Number;
  datePickerBgColor: StyleParamType.Color;
  datePickerBorderWidth: StyleParamType.Number;
  datePickerBorderRadius: StyleParamType.Number;
  datePickerBorderColor: StyleParamType.Color;
  datePickerMonthYearFont: StyleParamType.Font;
  datePickerMonthYearColor: StyleParamType.Color;
  datePickerDaysFont: StyleParamType.Font;
  datePickerDaysColor: StyleParamType.Color;
  datePickerSelectedBgColor: StyleParamType.Color;
  datePickerSelectedRadius: StyleParamType.Number;
  datePickerSelectedTextColor: StyleParamType.Color;
  datePickerTodayColor: StyleParamType.Color;
  datePickerUnavailableColor: StyleParamType.Color;
};

export default createStylesParams<IStylesParams>({
  pageAlignment: {
    type: StyleParamType.String,
    getDefaultValue: () => undefined as unknown as string,
    metadata: {
      type: StyleType.stringEnum,
      ai: {
        description: 'controls alignment of page content',
      },
      defaultValue: {
        type: 'static',
        value: PageAlignment.left,
      },
    },
  },
  pageBgColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  borderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.3),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  borderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  settingsBgColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const pageBgColor = params.getStyleParamValue({
        key: 'pageBgColor',
        type: StyleParamType.Color,
      });

      if (pageBgColor) {
        return pageBgColor;
      }

      return wixColorParam('color-1', 0)(params);
    },
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: {
        type: 'ref',
        ref: 'pageBgColor',
        fallback: wixColor(CssColor.WstPrimaryBackgroundColor),
      },
    },
  },
  fieldTheme: {
    type: StyleParamType.String,
    getDefaultValue: () => undefined as unknown as string,
    metadata: {
      type: StyleType.stringEnum,
      defaultValue: {
        type: 'static',
        value: TextFieldTheme.Box,
      },
      ai: {
        description: 'controls text field outline style',
      },
    },
  },
  fieldLabelColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  inputFieldsColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  fieldBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  inputFieldsFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  fieldLabelFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 14,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  descriptionFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      return wixFontParam('Body-M', {
        size: params.isMobile ? 14 : 16,
      })(params);
    },
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  descriptionColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  settingsFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  settingsColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  buttonTextFont: {
    type: StyleParamType.Font,
    getDefaultValue: wixFontParam('Body-M', {
      size: 16,
    }),
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  primaryButtonTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  buttonCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  buttonBgColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  buttonBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: (params) => {
      const buttonBgColor = params.getStyleParamValue({
        key: 'buttonBgColor',
        type: StyleParamType.Color,
      });

      if (buttonBgColor) {
        return buttonBgColor;
      }

      return wixColorParam('color-8')(params);
    },
    metadata: {
      type: StyleType.borderColor,
      defaultValue: {
        type: 'ref',
        ref: 'buttonBgColor',
        fallback: wixColor(CssColor.WstLinksAndActionsColor),
      },
    },
  },
  buttonBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  pageTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      return wixFontParam('Page-title', {
        size: params.isMobile ? 20 : 28,
        style: {
          bold: false,
          italic: false,
          underline: false,
        },
      })(params);
    },
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstHeading2Font),
    },
  },
  sectionTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  sectionTitleFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      return wixFontParam('Heading-XL', {
        size: params.isMobile ? 16 : 20,
        style: {
          bold: false,
          italic: false,
          underline: false,
        },
      })(params);
    },
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstHeading3Font),
    },
  },
  pageTitleColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  fieldRowGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 24,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 24,
      },
    },
  },
  fieldBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  fieldColGap: {
    type: StyleParamType.Number,
    getDefaultValue: () => 40,
    metadata: {
      type: StyleType.gap,
      defaultValue: {
        type: 'static',
        value: 40,
      },
    },
  },
  fieldsBgColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  dividerColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5', 0.2),
    metadata: {
      type: StyleType.borderBottomColor,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  dividerWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderBottomWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  inputCornerRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  datePickerBgColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  datePickerBorderWidth: {
    type: StyleParamType.Number,
    getDefaultValue: () => 1,
    metadata: {
      type: StyleType.borderWidth,
      defaultValue: {
        type: 'static',
        value: 1,
      },
    },
  },
  datePickerBorderRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 0,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 0,
      },
    },
  },
  datePickerBorderColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-37', 0.4),
    metadata: {
      type: StyleType.borderColor,
      defaultValue: wixColor(CssColor.WstBase2Color),
    },
  },
  datePickerMonthYearFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      return wixFontParam('Body-M', {
        size: 16,
        style: {
          bold: false,
          italic: false,
          underline: false,
        },
      })(params);
    },
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  datePickerMonthYearColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  datePickerDaysFont: {
    type: StyleParamType.Font,
    getDefaultValue: (params) => {
      return wixFontParam('Body-M', {
        size: 14,
        style: {
          bold: false,
          italic: false,
          underline: false,
        },
      })(params);
    },
    metadata: {
      type: StyleType.font,
      defaultValue: wixFont(CssFont.WstParagraph2Font),
    },
  },
  datePickerDaysColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-5'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstParagraph2Color),
    },
  },
  datePickerSelectedBgColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.backgroundColor,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  datePickerSelectedRadius: {
    type: StyleParamType.Number,
    getDefaultValue: () => 100,
    metadata: {
      type: StyleType.borderRadius,
      defaultValue: {
        type: 'static',
        value: 100,
      },
    },
  },
  datePickerSelectedTextColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-1'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstPrimaryBackgroundColor),
    },
  },
  datePickerTodayColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-8'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstLinksAndActionsColor),
    },
  },
  datePickerUnavailableColor: {
    type: StyleParamType.Color,
    getDefaultValue: wixColorParam('color-29'),
    metadata: {
      type: StyleType.color,
      defaultValue: wixColor(CssColor.WstShade2Color),
    },
  },
});

const getPageAlignment = (tpaData: any, styleParams: TPAParams) => {
  return (
    styleParams.strings.pageAlignment ??
    tpaData.pageAlignment ??
    PageAlignment.left
  );
};

const getFieldTheme = (
  tpaData: any,
  styleParams: TPAParams,
): TextFieldTheme => {
  return (
    styleParams.strings.fieldTheme ??
    tpaData.inputFieldsTheme ??
    TextFieldTheme.Box
  );
};

const getFieldVars = (tpaData: any, styleParams: TPAParams) => {
  const fieldTheme = getFieldTheme(tpaData, styleParams);
  const fieldBorderWidth = styleParams.numbers.fieldBorderWidth ?? 1;
  const fieldBorderCornerRadius = styleParams.numbers.inputCornerRadius ?? 0;

  const fieldVars = {
    [TextFieldTheme.Line]: {
      fieldBorderLeftWidth: 0,
      fieldBorderRightWidth: 0,
      fieldBorderTopWidth: 0,
      fieldBorderBottomWidth: fieldBorderWidth,
      fieldBorderCornerRadius: 0,
      fieldPaddingInline: 0,
      dropdownBeforeContentWidth: 0,
      dropdownAfterContentWidth: 0,
    },
    [TextFieldTheme.Box]: {
      fieldBorderLeftWidth: fieldBorderWidth,
      fieldBorderRightWidth: fieldBorderWidth,
      fieldBorderTopWidth: fieldBorderWidth,
      fieldBorderBottomWidth: fieldBorderWidth,
      fieldBorderCornerRadius,
      fieldPaddingInline: '12px',
      dropdownBeforeContentWidth: '12px',
      dropdownAfterContentWidth: '7px',
    },
  };

  return fieldVars[fieldTheme];
};

export const customCssVars: CustomCssVarsFn = ({
  tpaData,
  styleParams,
  isRTL,
}) => {
  const pageAlignment = getPageAlignment(tpaData, styleParams);
  const isPageAlignedRight = pageAlignment === PageAlignment.right;
  const isPageAlignedCenter = pageAlignment === PageAlignment.center;
  const isPageLayoutRtl = isPageAlignedRight ? !isRTL : isRTL;
  const pageTextAlign = isPageLayoutRtl ? 'right' : 'left';

  return {
    ...getFieldVars(tpaData, styleParams),
    pageDirection: isPageLayoutRtl ? 'rtl' : 'ltr',
    pageTextAlign,
    sectionTextAlign: isPageAlignedCenter ? 'center' : pageTextAlign,
    mobilePrivacyIconLeft: isPageAlignedRight ? '30px' : 'initial',
    mobilePrivacyIconRight: isPageAlignedRight ? 'initial' : '24px',
  };
};
